/**
 * Catálogo canônico de `bond.type` — ADR-037.
 *
 * Cada tenant declara quais tipos usa em `tenant_config`. NetComet inicia
 * com apenas `contract` (default). Tenants futuros (advocacia, clínica,
 * imobiliária etc.) adicionam tipos próprios.
 *
 * Defaults documentam o comportamento esperado quando o Bond é criado
 * sem override explícito.
 */
export const BOND_TYPES = [
    'contract',
    'customer-account',
    'opportunity',
    'procurement',
    'project',
    'legal-case',
    'mediation',
    'compliance-case',
    'patient-care',
    'student',
    'social-care-case',
    'home-care',
    'property',
    'vehicle',
    'equipment',
    'partnership',
    'vendor-relationship',
    'interest',
    'purchase-intent',
    'transaction-attempt',
    'rental-contract',
];
export const BOND_ADVERSARIAL_MODES = ['none', 'competitive', 'partial', 'strict'];
// Motor Neutro eixo 5 — o vocabulário da simetria da relação vive em
// identity-assurance/relation-symmetry (RELATION_SYMMETRIES/RelationSymmetry).
// NÃO re-exportar daqui: bonds/ carrega ANTES de identity-assurance/ no barrel raiz,
// e um import de valor aqui cria ciclo de module-load (exports parciais via __exportStar).
export const BOND_SIDES = ['seller', 'buyer', 'neutral', 'mediator'];
export const BOND_STATUSES = ['active', 'paused', 'closed', 'archived'];
export const BOND_LIFECYCLES = [
    'active-closed',
    'active-paused-closed',
    'open-resolved-closed',
    'in-care-discharged',
    'enrolled-graduated',
    'listed-sold',
    'in-progress-completed',
];
export const BOND_TYPES_CATALOG = [
    {
        id: 'contract',
        label: 'Contrato',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'active-closed',
    },
    {
        id: 'customer-account',
        label: 'Conta de cliente',
        defaultAdversarialMode: 'none',
        defaultSide: 'seller',
        defaultLifecycle: 'active-paused-closed',
    },
    {
        id: 'opportunity',
        label: 'Oportunidade',
        defaultAdversarialMode: 'none',
        defaultSide: 'seller',
        defaultLifecycle: 'active-closed',
    },
    {
        id: 'procurement',
        label: 'Aquisição',
        defaultAdversarialMode: 'competitive',
        defaultSide: 'buyer',
        defaultLifecycle: 'open-resolved-closed',
    },
    {
        id: 'project',
        label: 'Projeto',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'in-progress-completed',
    },
    {
        id: 'legal-case',
        label: 'Caso jurídico',
        defaultAdversarialMode: 'strict',
        defaultSide: null,
        defaultLifecycle: 'open-resolved-closed',
    },
    {
        id: 'mediation',
        label: 'Mediação',
        defaultAdversarialMode: 'partial',
        defaultSide: 'mediator',
        defaultLifecycle: 'open-resolved-closed',
    },
    {
        id: 'compliance-case',
        label: 'Caso de compliance',
        defaultAdversarialMode: 'partial',
        defaultSide: 'neutral',
        defaultLifecycle: 'open-resolved-closed',
    },
    {
        id: 'patient-care',
        label: 'Cuidado clínico',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'in-care-discharged',
    },
    {
        id: 'student',
        label: 'Aluno',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'enrolled-graduated',
    },
    {
        id: 'social-care-case',
        label: 'Caso socioassistencial',
        defaultAdversarialMode: 'none',
        defaultSide: 'neutral',
        defaultLifecycle: 'in-care-discharged',
    },
    {
        id: 'home-care',
        label: 'Cuidado domiciliar',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'in-care-discharged',
    },
    {
        id: 'property',
        label: 'Imóvel',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'listed-sold',
    },
    {
        id: 'vehicle',
        label: 'Veículo',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'listed-sold',
    },
    {
        id: 'equipment',
        label: 'Equipamento',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'active-closed',
    },
    {
        id: 'partnership',
        label: 'Parceria',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'active-closed',
    },
    {
        id: 'vendor-relationship',
        label: 'Relação com fornecedor',
        defaultAdversarialMode: 'none',
        defaultSide: 'buyer',
        defaultLifecycle: 'active-paused-closed',
    },
    {
        id: 'interest',
        label: 'Interesse',
        defaultAdversarialMode: 'competitive',
        defaultSide: null,
        defaultLifecycle: 'active-closed',
    },
    {
        id: 'purchase-intent',
        label: 'Intenção de compra',
        defaultAdversarialMode: 'competitive',
        defaultSide: 'buyer',
        defaultLifecycle: 'active-closed',
    },
    {
        id: 'transaction-attempt',
        label: 'Tentativa de transação',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'open-resolved-closed',
    },
    {
        id: 'rental-contract',
        label: 'Contrato de locação',
        defaultAdversarialMode: 'none',
        defaultSide: null,
        defaultLifecycle: 'active-closed',
    },
];
export const BOND_TYPES_BY_ID = Object.freeze(BOND_TYPES_CATALOG.reduce((acc, def) => {
    acc[def.id] = def;
    return acc;
}, {}));
export function getBondTypeDefinition(type) {
    return BOND_TYPES_BY_ID[type];
}
// ─────────────────────────────────────────────────────────────────────────
// Subjects — ADR-038 (schema-only nesta fatia, NetComet não usa).
// ─────────────────────────────────────────────────────────────────────────
export const SUBJECT_TYPES = [
    'person',
    'object-real-estate',
    'object-vehicle',
    'object-equipment',
    'concept-project',
    'concept-procurement',
    'concept-mediation-process',
];
export const SUBJECT_STATUSES = ['active', 'archived'];
// ─────────────────────────────────────────────────────────────────────────
// Roles default — ADR-039. Configuráveis por bond.type. NetComet usa apenas
// os de `contract`. Outros tenants override via tenant_config.
// ─────────────────────────────────────────────────────────────────────────
export const CONTRACT_BOND_ROLES = [
    'holder',
    'authorized-contact',
    'account-manager',
    'support-agent',
];
//# sourceMappingURL=catalog.js.map